import { useEffect, useMemo, useState, useRef } from 'react'

import classNames from 'classnames'
import { GetWorkspace_workspaceBySlug } from 'globalTypes'
import { useIntercom } from 'react-use-intercom'

import Sidebar from 'Root/Component/sidebar'
import { isMatching, listen } from 'lib/breakpoint-helper'
import { useCurrentUserId } from 'lib/use-current-user-id'
import { useHideUiForConsumer } from 'lib/use-hide-ui-for-consumer'
import { useIsClient } from 'lib/use-is-client'
import { useRouter } from 'next/router'

import { BoardContext } from './BoardContext'
import { LauncherContext } from './LauncherContext'

type PageLayoutProps = {
  children: React.ReactNode
  workspace?: GetWorkspace_workspaceBySlug
}

const PageLayout = ({ children, workspace }: PageLayoutProps) => {
  const { asPath } = useRouter()
  const { boot, update } = useIntercom()
  const currentUserId = useCurrentUserId()
  const bootedRef = useRef(false)
  const cleanPath = asPath.split('?')[0].split('#')[0]
  const isPathSegment = (segment: string) =>
    cleanPath === segment || cleanPath.startsWith(`${segment}/`)
  const isContentOrCollectionRoute = isPathSegment('/cc') || isPathSegment('/c')
  const mobileClosedByDefault =
    isContentOrCollectionRoute || isPathSegment('/f')
  const [boardOpen, setBoardOpen] = useState(() => !mobileClosedByDefault)
  const [isMobile, setIsMobile] = useState(false)
  const [launcherSuppressed, setLauncherSuppressed] = useState(false)
  const launcherContext = useMemo(() => ({ setLauncherSuppressed }), [])
  const isClient = useIsClient()
  const userId = useCurrentUserId()
  const hideBarInThisPage = isContentOrCollectionRoute

  const hideIntercomForConsumer = useHideUiForConsumer({
    hideSupportUI: workspace?.hideSupportUIForConsumer,
  })

  const showLauncherByRoute =
    cleanPath === '/' ||
    cleanPath === '/login' ||
    isPathSegment('/w') ||
    (isContentOrCollectionRoute && Boolean(currentUserId)) ||
    (isPathSegment('/f') && Boolean(currentUserId))

  const showLauncher =
    showLauncherByRoute && !hideIntercomForConsumer && !launcherSuppressed

  const showBoard = () => {
    if (isMobile) {
      setBoardOpen(true)
    }
  }

  const hideBoard = () => {
    if (isMobile) {
      setBoardOpen(false)
    }
  }

  useEffect(() => {
    setIsMobile(isMatching('sm', true))
    const listener = listen({ name: 'sm', useMax: true }, (mql) => {
      setIsMobile(mql.matches)
    })
    return () => listener.off()
  }, [])

  useEffect(() => {
    if (!isMobile) {
      return
    }

    setBoardOpen(!mobileClosedByDefault)
  }, [isMobile, mobileClosedByDefault])

  useEffect(() => {
    if (!bootedRef.current) {
      boot({
        hideDefaultLauncher: !showLauncher,
      })
      bootedRef.current = true
    } else {
      update({
        hideDefaultLauncher: !showLauncher,
      })
    }
  }, [showLauncher, boot, update])

  if (!userId || !isClient) {
    return children
  }

  return (
    <BoardContext.Provider
      value={{
        boardOpen,
        showBoard,
        hideBoard,
        showSidebarButton: isMobile,
      }}
    >
      <div
        className={classNames('flex min-h-screen relative', {
          'overflow-hidden': isMobile && boardOpen,
          'overflow-visible': !isMobile,
          'pl-6 pt-6': !isMobile && !hideBarInThisPage,
        })}
      >
        {userId && isClient && (isMobile || !hideBarInThisPage) && (
          <div
            id="sidebar-wrapper"
            className={classNames([
              'w-full sm:w-72 transform transition-transform duration-500 ease-in-out sm:rounded-2xl',
              isMobile
                ? boardOpen
                  ? 'z-20 translate-x-0'
                  : 'z-10 -translate-x-full'
                : 'z-10',
            ])}
            style={{
              position: isMobile ? 'fixed' : 'sticky',
              top: isMobile ? 0 : 24,
              left: 0,
              height: isMobile ? '100dvh' : 'calc(100dvh - 48px)',
              zIndex: isMobile ? 30 : 10,
            }}
          >
            <Sidebar />
          </div>
        )}

        <div
          id="page-content"
          className="flex-1 flex flex-col sm:static min-h-screen transition-transform duration-500 ease-in-out"
        >
          <LauncherContext.Provider value={launcherContext}>
            {children}
          </LauncherContext.Provider>
        </div>
      </div>
    </BoardContext.Provider>
  )
}

export default PageLayout
